import { useState, useRef, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import { MessageSquare } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useDispatch, useSelector } from "react-redux";
import { openAuthDialog } from "../store/auth/authSlice";
import {
  fetchMessages,
  SellerMessagesCount,
  sendMessage,
  updateIsRead
} from "../store/partChat/partChatActions";
import ChatMessages from "./messages/ChatMessgaes";
import FilePreview from "./messages/FilePreview";
import ChatInput from "./messages/ChatInput";
import MediaPreviewDialog from "./messages/MediaPreviewDialog";
import { FileHandler } from "./messages/FileHandler";

const PartChatDialog = ({
  children,
  messages,
  conversation,
  isMessagesLoading = false,
  isSenderLoading = false,
  isOpen: controlledIsOpen,
  onOpenChange: controlledOnOpenChange
}) => {
  console.log("conversation: ", conversation);
  const dispatch = useDispatch();
  const scrollAreaRef = useRef(null);
  const { toast } = useToast();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [previewFile, setPreviewFile] = useState(null);
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { selectedFiles, handleFileSelect, removeFile, clearFiles } =
    FileHandler();
  const { sellerMessagesCount, isSellerCountLoading } = useSelector(
    (state) => state.partChat
  );

  const userId = user?.uid;
  const isOpen =
    controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = controlledOnOpenChange || setInternalIsOpen;
  const part = conversation?.partInfo;
  console.log("part: ", part);
  const seller = conversation?.otherUserDetail;
  const isInquiryChat = conversation?.partId === "inquiry";

  const receiverId = seller?.uid || seller?.id;

  const sellerDisplayName =
    user?.role === "buyer"
      ? seller?.companyName || seller?.name
      : seller?.companyName || seller?.name || seller?.displayName;
  const sellerAvatarUrl = seller?.photoURL;

  const displayPrice = (() => {
    if (isInquiryChat) return null;
    if (typeof part?.price === "number") return part.price.toFixed(2);
    if (typeof part?.price === "string" && !isNaN(parseFloat(part.price)))
      return parseFloat(part.price).toFixed(2);
    return "N/A";
  })();

  const handleInputValueChange = useCallback(
    (e) => setInputValue(e.target.value),
    []
  );

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const text = inputValue.trim();
    const hasFiles = selectedFiles.length > 0;

    if ((!text && !hasFiles) || isSenderLoading || !receiverId) {
      if (!isAuthenticated) {
        toast({
          title: "Login Required",
          description: "Please login to send a message.",
          variant: "destructive"
        });
        dispatch(
          openAuthDialog({
            showLoginFirstOrOnSuccess: true,
            redirectPage: window.location.pathname,
            onSuccessfulAuth: () => setIsOpen(true)
          })
        );
      }
      return;
    }

    // ✅ Fixed: Use receiverId and ensure partId is set correctly
    const payload = {
      senderId: userId,
      receiverId: receiverId,
      partId:
        part?.id || conversation.partName.toLowerCase().replace(/\s+/g, "-"),
      content: text,
      files: selectedFiles,
      partName: part?.name
    };

    setInputValue("");
    clearFiles();

    try {
      await dispatch(sendMessage(payload)).unwrap();
      if (messages?.length == 0) {
        dispatch(
          fetchMessages({
            sellerId: user?.role === "seller" ? userId : receiverId,
            buyerId: user?.role === "buyer" ? userId : receiverId,
            partId:
              part?.id ||
              conversation.partName.toLowerCase().replace(/\s+/g, "-"),
            currentUserId: user?.uid
          })
        );
      }
    } catch (error) {
      console.error("Failed to send message:", error.message);
      toast({
        title: "Failed trying to send message",
        description: "Please try again",
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    if (
      isOpen &&
      messages.length > 0 &&
      userId &&
      receiverId &&
      conversation?.partId
    ) {
      dispatch(
        updateIsRead({
          currentUserId: userId,
          receiverId: receiverId,
          partId: conversation.partId
        })
      );
    }
  }, [
    messages.length,
    isOpen,
    userId,
    receiverId,
    conversation?.partId,
    dispatch
  ]);

  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollViewport = scrollAreaRef.current.querySelector(
        "[data-radix-scroll-area-viewport]"
      );
      if (scrollViewport) {
        setTimeout(() => {
          scrollViewport.scrollTop = scrollViewport.scrollHeight;
        }, 50);
      }
    }
  }, [messages]);

  useEffect(() => {
    if (user?.role === "seller") {
      dispatch(SellerMessagesCount(user?.uid));
    }
  }, [dispatch, user?.role, user?.uid]);

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (!open) {
            setInputValue("");
            clearFiles();
            setPreviewFile(null);
          }
        }}
      >
        <DialogTrigger asChild>{children}</DialogTrigger>
        <DialogContent className="sm:max-w-[450px] h-[70vh] flex flex-col px-3 sm:px-0 rounded-xl">
          <DialogHeader className="p-4 border-b">
            <DialogTitle className="flex items-center gap-2 text-primary">
              <MessageSquare className="h-5 w-5 text-primary" />{" "}
              {sellerDisplayName || "User"}
              <span className="text-xs mt-1 text-muted-foreground italic sm:ml-3">
                {(() => {
                  const userRead = conversation?.isRead?.find(
                    (r) => r.readBy === conversation.otherUserDetail?.id
                  );

                  if (userRead?.readAt) {
                    const date = userRead.readAt.toDate();
                    const diffMs = Date.now() - date.getTime();
                    const diffMinutes = Math.floor(diffMs / (1000 * 60));

                    if (diffMinutes < 1) return "Last Seen just now";
                    if (diffMinutes < 60)
                      return `Last Seen ${diffMinutes} min ago`;

                    const diffHours = Math.floor(diffMinutes / 60);
                    if (diffHours < 24) return `Last Seen ${diffHours}h ago`;

                    const diffDays = Math.floor(diffHours / 24);
                    if (diffDays === 1) return "Last Seen yesterday";
                    if (diffDays < 7) return `Last Seen ${diffDays}d ago`;

                    return `Last Seen on ${date.toLocaleDateString()}`;
                  }

                  return null;
                })()}
              </span>
            </DialogTitle>
            <DialogDescription>
              {isInquiryChat ? (
                <>
                  Inquiry about:{" "}
                  <span className="font-semibold text-foreground">
                    {conversation?.partName || "General Inquiry"}
                  </span>
                </>
              ) : (
                <>
                  Regarding:{" "}
                  <span className="font-semibold text-foreground">
                    {part?.name
                      ? part?.name == "other"
                        ? part?.sub_name
                        : part?.name
                      : conversation?.partName || "Part"}
                  </span>{" "}
                  {displayPrice &&
                    displayPrice !== "N/A" &&
                    `($${displayPrice})`}
                </>
              )}
              <br />
            </DialogDescription>
          </DialogHeader>

          <ChatMessages
            messages={messages}
            isLoading={isMessagesLoading}
            userId={userId}
            userAvatar={user?.photoURL}
            userName={user?.displayName}
            otherUserAvatar={sellerAvatarUrl}
            otherUserName={sellerDisplayName}
            onMediaClick={setPreviewFile}
            scrollAreaRef={scrollAreaRef}
            sellerMessagesCount={sellerMessagesCount}
            isSellerCountLoading={isSellerCountLoading}
            messagesLimitCheck={
              user?.role === "seller" &&
              sellerMessagesCount?.planId === "free" &&
              Number(sellerMessagesCount?.count ?? 0) >= 30 &&
              !!sellerMessagesCount?.currentPeriodEnd &&
              new Date(sellerMessagesCount.currentPeriodEnd).getTime() >
                Date.now()
            }
          />

          <FilePreview
            files={selectedFiles}
            onRemoveFile={removeFile}
            onFileClick={setPreviewFile}
          />

          <DialogFooter className="p-4 border-t flex flex-col space-y-3">
            <ChatInput
              inputValue={inputValue}
              onInputChange={handleInputValueChange}
              onSubmit={handleSendMessage}
              onFileSelect={handleFileSelect}
              isSending={isSenderLoading}
              isLoading={isMessagesLoading}
              isAuthenticated={isAuthenticated}
              hasFiles={selectedFiles.length > 0}
              isSellerCountLoading={isSellerCountLoading}
              messageLimitCheck={
                user?.role === "seller" &&
                sellerMessagesCount?.planId === "free" &&
                Number(sellerMessagesCount?.count ?? 0) >= 30 &&
                !!sellerMessagesCount?.currentPeriodEnd &&
                new Date(sellerMessagesCount.currentPeriodEnd).getTime() >
                  Date.now()
              }
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MediaPreviewDialog
        previewFile={previewFile}
        onClose={() => setPreviewFile(null)}
      />
    </>
  );
};

export default PartChatDialog;
